import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { OrgAlert } from '@/components/common/OrgAlert';
import { useOrgs } from '@/contexts/OrgsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useGetAgents } from '@/api/control/control-plane/control-plane';
import { useAgentManifest } from '@/hooks/useAgentManifest';
import { isVersionOutdated } from '@/services/versionUtils';

/**
 * Dashboard alert card that warns when agents are running outdated versions.
 * Returns null if all agents are current.
 */
export function AgentUpdateAlert() {
  const { selectedOrganizationId } = useOrgs();
  const { t } = useLanguage();
  const { latestVersion } = useAgentManifest();

  const { data: agentsResponse } = useGetAgents(selectedOrganizationId ?? '', {
    query: {
      enabled: !!selectedOrganizationId,
    },
  });

  const outdatedAgents = useMemo(() => {
    const agents = agentsResponse?.data ?? [];
    return agents.filter((agent) => isVersionOutdated(agent.version, latestVersion));
  }, [agentsResponse, latestVersion]);

  // Return null if no outdated agents
  if (outdatedAgents.length === 0) {
    return null;
  }

  return (
    <Card className="flex-shrink-0 w-fit min-w-[350px]">
      <CardHeader className="pb-3">
        <CardTitle>{t.agents.updateAvailable}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-3">
        <OrgAlert variant="warning" title={t.agents.updateAvailable}>
          <p className="text-xs">
            {t.agents.updateAvailableDescription} (v{latestVersion})
          </p>
          <ul className="text-xs list-disc list-inside">
            {outdatedAgents.map((agent) => (
              <li key={agent.id}>
                <strong>{agent.name}</strong> (v{agent.version})
              </li>
            ))}
          </ul>
          <div className="mt-auto self-end">
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-warning text-warning hover:opacity-90"
            >
              <Link to={`/dashboard/orgs/${selectedOrganizationId ?? ''}/agents`}>
                {t.sidebar.agents}
                <ArrowRight className="ml-2 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </OrgAlert>
      </CardContent>
    </Card>
  );
}
